import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus,
  Calendar,
  MapPin,
  Search,
  Filter,
  MoreVertical,
  Pencil,
  Trash2,
  ExternalLink,
  Monitor
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Skeleton } from "@/components/ui/skeleton";

export default function Events() {
  const { user, profile, loading: authLoading } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const isAdmin = profile?.role === 'admin';

  useEffect(() => {
    if (!authLoading) loadEvents();
  }, [authLoading]);

  const loadEvents = async () => {
    try {
      const { data } = await supabase
        .from('events')
        .select('*')
        .order('start_date', { ascending: true, nullsFirst: false });

      if (data) setEvents(data);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setEventsLoading(false);
    }
  };

  const deleteEvent = async (id: string) => {
    await supabase.from('events').delete().eq('id', id);
    setEvents(events.filter(e => e.id !== id));
    setDeleteId(null);
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      planning: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      confirmed: 'bg-primary-500/10 text-primary-400 border-primary-500/20',
      in_progress: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      completed: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
      cancelled: 'bg-red-500/10 text-red-400 border-red-500/20',
    };
    return styles[status] || styles.planning;
  };

  if (authLoading || eventsLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Skeleton className="h-8 w-32 mb-2 bg-zinc-800" />
          <Skeleton className="h-4 w-48 bg-zinc-800" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-5">
              <Skeleton className="h-10 w-10 rounded-lg mb-4 bg-zinc-800" />
              <Skeleton className="h-5 w-3/4 mb-2 bg-zinc-800" />
              <Skeleton className="h-4 w-1/2 bg-zinc-800" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Events</h1>
          <p className="text-zinc-500 mt-1">Manage your production events</p>
        </div>
        {isAdmin && (
          <Link to={createPageUrl('CreateEvent')}>
            <Button className="bg-primary-500 text-white hover:bg-primary-600 border-primary-500">
              <Plus className="w-4 h-4 mr-2" />
              New Event
            </Button>
          </Link>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40 bg-zinc-900 border-zinc-800 text-white">
            <Filter className="w-4 h-4 mr-2 text-zinc-500" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="planning">Planning</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-zinc-900 rounded-2xl flex items-center justify-center mx-auto mb-4">
            {search || statusFilter !== 'all' || events.length > 0 ? (
              <Monitor className="w-8 h-8 text-zinc-600" />
            ) : (
              <svg className="w-8 h-8 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            )}
          </div>
          <h3 className="text-lg font-medium text-white mb-2">
            {search || statusFilter !== 'all' || events.length > 0
              ? 'No events found'
              : isAdmin
              ? 'No events created yet'
              : 'Waiting for Event Access'}
          </h3>
          <p className="text-zinc-500 mb-6">
            {search || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : events.length > 0
              ? 'No events match your current filter'
              : isAdmin
              ? 'Create your first event to get started'
              : 'Your account has been created successfully. An administrator will add you to events soon.'}
          </p>
          {!search && statusFilter === 'all' && events.length === 0 && isAdmin && (
            <Link to={createPageUrl('CreateEvent')}>
              <Button className="bg-primary-500 text-white hover:bg-primary-600 border-primary-500">
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEvents.map((event, index) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <div className="group bg-[#1a1a1a]/50 border border-zinc-800/50 rounded p-5 hover:border-primary-500/50 hover:bg-[#1a1a1a] transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <Link
                    to={createPageUrl('EventPortal') + `?eventId=${event.id}&section=overview`}
                    className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden transition-colors"
                  >
                    {event.avatar_url ? (
                      <img
                        src={event.avatar_url}
                        alt={event.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-zinc-800 rounded flex items-center justify-center group-hover:bg-zinc-700">
                        <Monitor className="w-5 h-5 text-zinc-400 group-hover:text-primary-500" />
                      </div>
                    )}
                  </Link>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 text-xs rounded-full border ${getStatusBadge(event.status)}`}>
                      {event.status?.replace('_', ' ') || 'Planning'}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-500 hover:text-white hover:bg-zinc-800">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                        <DropdownMenuItem asChild className="text-zinc-300 focus:text-white focus:bg-zinc-800">
                          <Link to={createPageUrl('EventPortal') + `?eventId=${event.id}&section=overview`}>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open Portal
                          </Link>
                        </DropdownMenuItem>
                        {isAdmin && (
                          <>
                            <DropdownMenuItem asChild className="text-zinc-300 focus:text-white focus:bg-zinc-800">
                              <Link to={createPageUrl('EditEvent') + `?eventId=${event.id}`}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-zinc-800" />
                            <DropdownMenuItem
                              onClick={() => setDeleteId(event.id)}
                              className="text-red-400 focus:text-red-400 focus:bg-zinc-800"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <Link to={createPageUrl('EventPortal') + `?eventId=${event.id}&section=overview`}>
                  <h3 className="text-lg font-medium text-white mb-3 group-hover:text-zinc-100">
                    {event.name}
                  </h3>
                </Link>

                <div className="space-y-2">
                  {event.start_date && (
                    <div className="flex items-center gap-2 text-sm text-zinc-500">
                      <Calendar className="w-3.5 h-3.5" />
                      {format(new Date(event.start_date), 'MMM d, yyyy')}
                      {event.end_date && event.end_date !== event.start_date && (
                        <span>– {format(new Date(event.end_date), 'MMM d')}</span>
                      )}
                    </div>
                  )}
                  {event.venues?.[0]?.name && (
                    <div className="flex items-center gap-2 text-sm text-zinc-500">
                      <MapPin className="w-3.5 h-3.5" />
                      <span className="truncate">{event.venues[0].name}</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Event?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently delete this event and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteEvent(deleteId)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
